import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { TaskReminderPayload } from '../tasks.types';
import {
  fromPickerValues,
  REMINDER_UNITS,
  ReminderUnit,
  splitReminderMinutes,
  TASK_REMINDER_MAX_MINUTES,
  toReminderMinutes,
} from '../tasks.utils';

/**
 * A reminder as the list shows it: a saved one of a task, or one of a task that is not created yet,
 * which has not been sent
 */
export interface TaskReminderRow {
  id: string;
  /** Minutes before the deadline for a reminder that follows it, none for one at an exact time */
  before_deadline_minutes: number | null;
  remind_at: string | null;
  sent_at: string | null;
}

type ReminderMode = 'before' | 'at';

/**
 * The reminders of a task: the list with the ones that are sent, and a row to add one more, either some time before
 * the deadline or at an exact time. It only asks: the owner of the list adds and removes the reminders.
 */
@Component({
  selector: 'app-task-reminders',
  templateUrl: './task-reminders.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block' },
})
export class TaskRemindersComponent implements OnChanges {
  @Input() reminders: readonly TaskReminderRow[] = [];
  /** A reminder before the deadline needs one */
  @Input() hasDeadline = false;
  /** A request of the owner is running */
  @Input() busy = false;
  @Output() add = new EventEmitter<TaskReminderPayload>();
  @Output() remove = new EventEmitter<TaskReminderRow>();

  protected readonly units = REMINDER_UNITS;
  protected readonly splitReminderMinutes = splitReminderMinutes;

  protected readonly mode = new FormControl<ReminderMode>('before', { nonNullable: true });
  /** A number input gives the control a number, or null when it is empty */
  protected readonly amount = new FormControl<number | null>(3);
  protected readonly unit = new FormControl<ReminderUnit>('hours', { nonNullable: true });
  protected readonly date = new FormControl<string | null>('');
  /** The time belongs to a date: it is disabled until there is one */
  protected readonly time = new FormControl<string | null>({ value: '', disabled: true });
  protected invalid = false;

  protected modeOptions = this.getModeOptions();
  protected readonly unitOptions = REMINDER_UNITS.map((value) => ({
    value,
    label: `crm_tasks.reminders.unit.${value}`,
  }));

  constructor() {
    this.date.valueChanges.pipe(takeUntilDestroyed()).subscribe((date) => this.syncTime(date));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['hasDeadline']) return;

    this.modeOptions = this.getModeOptions();
    // Without a deadline nothing can be set before it
    if (!this.hasDeadline && this.mode.value === 'before') this.mode.setValue('at');
  }

  protected submit(): void {
    const payload = this.mode.value === 'before' ? this.getBeforeDeadline() : this.getExactTime();
    this.invalid = !payload;
    if (!payload || this.busy) return;

    this.add.emit(payload);
    this.date.reset('');
  }

  /** A picked date starts at midnight and a cleared one takes its time with it */
  private syncTime(date: string | null): void {
    if (date) {
      this.time.enable({ emitEvent: false });
      if (!this.time.value) this.time.setValue('00:00', { emitEvent: false });
      return;
    }

    this.time.reset('', { emitEvent: false });
    this.time.disable({ emitEvent: false });
  }

  private getModeOptions(): { value: ReminderMode; label: string; disabled: boolean }[] {
    return [
      { value: 'before', label: 'crm_tasks.reminders.mode_before', disabled: !this.hasDeadline },
      { value: 'at', label: 'crm_tasks.reminders.mode_at', disabled: false },
    ];
  }

  private getBeforeDeadline(): TaskReminderPayload | null {
    const amount = this.amount.value;
    if (amount === null || !Number.isInteger(amount) || amount < 0 || !this.hasDeadline) return null;

    const minutes = toReminderMinutes(amount, this.unit.value);
    return minutes <= TASK_REMINDER_MAX_MINUTES ? { before_deadline_minutes: minutes } : null;
  }

  private getExactTime(): TaskReminderPayload | null {
    const remindAt = fromPickerValues(this.date.value, this.time.value);
    return remindAt ? { remind_at: remindAt } : null;
  }
}
